import { CacheLookupPolicy, LogLevel, type Configuration } from '@azure/msal-browser';
import type { AuthPlugin, ExternalAuthPlugin } from '@cdf/api-client-react';

export type AuthProviderKind = 'workforce' | 'external';

export interface BrowserAuthProviderConfig {
  kind: AuthProviderKind;
  msalConfig: Configuration;
  loginRequest: {
    scopes: string[];
    redirectUri: string;
  };
  apiTokenRequest: {
    scopes: string[];
    redirectUri: string;
  };
  redirectUri: string;
  /** 'popup' only when a dedicated redirect bridge is configured. */
  interactionMode: 'redirect' | 'popup';
  /** Dedicated MSAL redirect bridge for popup and silent requests only. */
  popupRedirectUri: string | null;
}

/** Options for every silent token request made by this provider. */
export interface SilentTokenOptions {
  redirectUri: string;
  cacheLookupPolicy?: CacheLookupPolicy;
}

/**
 * With a bridge, the default lookup (cache, refresh token, then the hidden
 * bridge iframe) renews quietly. Without one, never let a hidden iframe load
 * the whole application.
 */
export function silentTokenOptions(config: BrowserAuthProviderConfig): SilentTokenOptions {
  return config.popupRedirectUri
    ? { redirectUri: config.popupRedirectUri }
    : { redirectUri: config.redirectUri, cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken };
}

export const AUTH_PROVIDER_STORAGE_KEY = 'cdf:auth-provider';

function splitScopes(value: string | undefined): string[] {
  return (value ?? '').split(/\s+/).filter(Boolean);
}

function oidcScopes(value: string | undefined): string[] {
  const allowed = new Set(['openid', 'profile', 'email', 'offline_access']);
  const configured = splitScopes(value).filter((scope) => allowed.has(scope));
  return configured.length > 0 ? configured : ['openid', 'profile', 'email', 'offline_access'];
}

function unique(values: readonly string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

export function rootRedirectUri(value: string | undefined): string {
  const configured = new URL(value || window.location.origin, window.location.origin);
  return `${configured.origin}/`;
}

/**
 * The bridge path is kept exactly as configured (never normalized to `/`).
 * Anything off this app's origin selects the legacy root-redirect contract.
 */
export function bridgeRedirectUri(mode: string | undefined, value: string | undefined): string | null {
  if (mode !== 'popup' || !value) return null;
  try {
    const url = new URL(value);
    if (
      url.origin !== window.location.origin || url.pathname === '/' ||
      url.username || url.password || url.search || url.hash
    ) {
      return null;
    }
    return url.toString();
  } catch {
    return null;
  }
}

const workforceConfiguredRedirectUri = import.meta.env.ENTRA_REDIRECT_URI;
const externalConfiguredRedirectUri = import.meta.env.ENTRA_EXTERNAL_REDIRECT_URI;
const configuredApiScopes = splitScopes(
  import.meta.env.ENTRA_API_SCOPES ||
    import.meta.env.ENTRA_API_SCOPE
);
const configuredExternalApiScopes = splitScopes(
  import.meta.env.ENTRA_EXTERNAL_API_SCOPES || import.meta.env.ENTRA_EXTERNAL_API_SCOPE
);

function createProviderConfig(input: {
  kind: AuthProviderKind;
  clientId: string;
  authority: string;
  configuredRedirectUri: string;
  signInScopes: string[];
  apiScopes: string[];
  interactionMode?: string;
  popupRedirectUri?: string;
}): BrowserAuthProviderConfig {
  const providerRedirectUri = rootRedirectUri(input.configuredRedirectUri);
  const bridge = bridgeRedirectUri(input.interactionMode, input.popupRedirectUri);
  return {
    kind: input.kind,
    msalConfig: {
      auth: {
        clientId: input.clientId,
        authority: input.authority,
        redirectUri: providerRedirectUri,
        // Full-page flows always return to the registered root.
        postLogoutRedirectUri: providerRedirectUri
      },
      cache: {
        // The preview iframe and its top-level sign-in tab share this same-origin
        // MSAL-managed cache; application code never reads or stores tokens itself.
        cacheLocation: 'localStorage'
      },
      system: {
        loggerOptions: {
          logLevel: LogLevel.Warning,
          piiLoggingEnabled: false
        }
      }
    },
    loginRequest: {
      scopes: unique([...input.signInScopes, ...input.apiScopes]),
      redirectUri: providerRedirectUri
    },
    apiTokenRequest: {
      scopes: input.apiScopes,
      redirectUri: providerRedirectUri
    },
    redirectUri: providerRedirectUri,
    interactionMode: bridge ? 'popup' : 'redirect',
    popupRedirectUri: bridge
  };
}

export let entraEnabled = import.meta.env.ENTRA_ENABLED === 'true';
export let entraConfigurationComplete = Boolean(
  import.meta.env.ENTRA_CLIENT_ID && import.meta.env.ENTRA_AUTHORITY
);
export let externalEntraEnabled = import.meta.env.ENTRA_EXTERNAL_ENABLED === 'true';
export let externalEntraConfigurationComplete = Boolean(
  externalEntraEnabled &&
  import.meta.env.ENTRA_EXTERNAL_CLIENT_ID &&
  import.meta.env.ENTRA_EXTERNAL_AUTHORITY &&
  import.meta.env.ENTRA_EXTERNAL_REDIRECT_URI &&
  configuredExternalApiScopes.length > 0
);

export let workforceAuthProvider = createProviderConfig({
  kind: 'workforce',
  clientId: import.meta.env.ENTRA_CLIENT_ID,
  authority: import.meta.env.ENTRA_AUTHORITY,
  configuredRedirectUri: workforceConfiguredRedirectUri,
  signInScopes: oidcScopes(import.meta.env.ENTRA_SCOPES),
  apiScopes: configuredApiScopes
});

export let externalAuthProvider: BrowserAuthProviderConfig | null =
  externalEntraConfigurationComplete
    ? createProviderConfig({
        kind: 'external',
        clientId: import.meta.env.ENTRA_EXTERNAL_CLIENT_ID,
        authority: import.meta.env.ENTRA_EXTERNAL_AUTHORITY,
        configuredRedirectUri: externalConfiguredRedirectUri,
        signInScopes: oidcScopes(import.meta.env.ENTRA_EXTERNAL_SCOPES),
        apiScopes: configuredExternalApiScopes
      })
    : null;

/** Runtime settings are authoritative, including for builds with no Entra values. */
export function configureWorkforceAuth(provider: AuthPlugin | undefined): void {
  entraEnabled = false;
  entraConfigurationComplete = false;
  if (!provider || typeof provider.enabled !== 'boolean' || provider.status === 'misconfigured') {
    throw new Error('Staff sign-in configuration could not be loaded.');
  }
  if (!provider.enabled && provider.status === 'disabled') return;
  if (!provider.enabled || provider.status !== 'ready') {
    throw new Error('Staff sign-in configuration is unavailable.');
  }
  const config = provider.config;
  if (
    !config ||
    typeof config.clientId !== 'string' || !config.clientId.trim() ||
    typeof config.authority !== 'string' ||
    typeof config.redirectUri !== 'string' ||
    !Array.isArray(config.scopes) || !config.scopes.every((scope) => typeof scope === 'string') ||
    !Array.isArray(config.apiScopes) || config.apiScopes.length === 0 ||
    !config.apiScopes.every((scope) => typeof scope === 'string' && /^api:\/\/[^\s]+\/[^\s/]+$/.test(scope))
  ) {
    throw new Error('Staff sign-in configuration is incomplete.');
  }
  const authority = new URL(config.authority);
  const redirect = new URL(config.redirectUri);
  if (authority.protocol !== 'https:' || authority.username || authority.password ||
      redirect.origin !== window.location.origin || redirect.username || redirect.password) {
    throw new Error('Staff sign-in configuration contains an invalid address.');
  }
  workforceAuthProvider = createProviderConfig({
    kind: 'workforce',
    clientId: config.clientId,
    authority: config.authority,
    configuredRedirectUri: config.redirectUri,
    signInScopes: oidcScopes(config.scopes.join(' ')),
    apiScopes: config.apiScopes,
    interactionMode: config.interactionMode,
    popupRedirectUri: config.popupRedirectUri
  });
  msalConfig = workforceAuthProvider.msalConfig;
  loginRequest = workforceAuthProvider.loginRequest;
  apiTokenRequest = workforceAuthProvider.apiTokenRequest;
  redirectUri = workforceAuthProvider.redirectUri;
  entraEnabled = true;
  entraConfigurationComplete = true;
}

/** Called once during bootstrap, before constructing MSAL or rendering React. */
export function configureExternalAuth(provider: ExternalAuthPlugin | undefined): void {
  externalAuthProvider = null;
  externalEntraConfigurationComplete = false;
  externalEntraEnabled = false;
  if (!provider || typeof provider.enabled !== 'boolean') {
    throw new Error('Customer sign-in configuration could not be loaded.');
  }
  if (!provider.enabled || provider.status !== 'ready') return;
  const config = provider.config;
  if (
    !config ||
    typeof config.clientId !== 'string' || !config.clientId.trim() ||
    typeof config.authority !== 'string' ||
    typeof config.redirectUri !== 'string' ||
    !Array.isArray(config.scopes) || !config.scopes.every((scope) => typeof scope === 'string') ||
    !Array.isArray(config.apiScopes) || config.apiScopes.length === 0 ||
    !config.apiScopes.every((scope) => typeof scope === 'string' && /^api:\/\/[^\s]+\/[^\s/]+$/.test(scope))
  ) {
    throw new Error('Customer sign-in configuration is incomplete.');
  }
  const authority = new URL(config.authority);
  const redirect = new URL(config.redirectUri);
  if (authority.protocol !== 'https:' || authority.username || authority.password ||
      redirect.origin !== window.location.origin || redirect.username || redirect.password) {
    throw new Error('Customer sign-in configuration contains an invalid address.');
  }
  externalAuthProvider = createProviderConfig({
    kind: 'external',
    clientId: config.clientId,
    authority: config.authority,
    configuredRedirectUri: config.redirectUri,
    signInScopes: oidcScopes(config.scopes.join(' ')),
    apiScopes: config.apiScopes,
    interactionMode: config.interactionMode,
    popupRedirectUri: config.popupRedirectUri
  });
  externalEntraEnabled = true;
  externalEntraConfigurationComplete = true;
}

export function selectAuthProvider(
  requested: string | null,
  stored: string | null,
  externalAvailable = externalEntraConfigurationComplete
): AuthProviderKind {
  if (requested === 'workforce') return 'workforce';
  if (requested === 'external' && externalAvailable) return 'external';
  if (stored === 'external' && externalAvailable) return 'external';
  return 'workforce';
}

export function persistAuthProvider(provider: AuthProviderKind): void {
  window.localStorage.setItem(AUTH_PROVIDER_STORAGE_KEY, provider);
}

export function resolveInitialAuthProvider(): AuthProviderKind {
  const url = new URL(window.location.href);
  const requested = url.searchParams.get('authProvider');
  const stored = window.localStorage.getItem(AUTH_PROVIDER_STORAGE_KEY);
  const selected = selectAuthProvider(requested, stored);
  if (requested === selected) persistAuthProvider(selected);
  return selected;
}

export function getAuthProviderConfig(
  provider: AuthProviderKind
): BrowserAuthProviderConfig | null {
  return provider === 'external' ? externalAuthProvider : workforceAuthProvider;
}

// Compatibility aliases for existing workforce-only consumers.
export let msalConfig = workforceAuthProvider.msalConfig;
export let loginRequest = workforceAuthProvider.loginRequest;
export let apiTokenRequest = workforceAuthProvider.apiTokenRequest;
export let redirectUri = workforceAuthProvider.redirectUri;
